<script lang="ts">
import { defineComponent, useSlots, computed } from 'vue'
import ModalCore from '@/components/modal/ModalCore.vue'
const { props, emits } = ModalCore

export default defineComponent({
  name: 'ModalDefault',

  components: {
    ModalCore,
  },

  props,

  emits,

  setup() {
    const slots = useSlots()

    const hasHeader = computed<boolean>(() => {
      return !!slots.header
    })

    const hasDescription = computed<boolean>(() => {
      return !!slots.description
    })

    const hasAction = computed<boolean>(() => {
      return !!slots.action
    })

    const hasFooter = computed<boolean>(() => {
      return !!slots.footer
    })

    return {
      // computed
      hasHeader,
      hasDescription,
      hasAction,
      hasFooter,
    }
  },
})
</script>

<template>
  <ModalCore
    :title="title"
    :header-message="headerMessage"
    :header-message-alignment="headerMessageAlignment"
    :is-show="isShow"
    :is-black-out="isBlackOut"
    :is-blur="isBlur"
    :enable-backdrop="enableBackdrop"
    :enable-close="enableClose"
    :size="size"
    :state-style-class="stateStyleClass"
    :additional-style-class="additionalStyleClass"
    :scroll-to="scrollTo"
    @update:is-show="$emit('update:isShow', $event)"
    @complete:scroll="$emit('complete:scroll')"
    @close="$emit('close')">
    <template
      v-if="hasHeader"
      #header>
      <slot name="header" />
    </template>

    <template
      v-if="hasDescription"
      #description>
      <slot name="description" />
    </template>

    <template
      v-if="hasAction"
      #action>
      <slot name="action" />
    </template>

    <template
      v-if="hasFooter"
      #footer>
      <slot name="footer" />
    </template>
  </ModalCore>
</template>

<style lang="sass" scoped>
/**
 * deep 修飾子で ModalCore.vue にスタイルを注入
 * @see https://zenn.dev/karan_coron/articles/87597250de406018e351
 */
// layout
:deep .l-unit__modal
  $root: '.l-unit__modal'
  $gutter-size: 16px
  $modal-padding: 30px
  width: 100%
  min-height: 56px
  max-height: 100%
  // overflow: hidden
  position: relative
  display: grid
  grid-template-rows: auto 1fr auto auto
  color: $font-black
  margin: 20px auto
  padding: $modal-padding
  background-color: rgba($bg-white, 1)
  border-radius: $border-radius
  box-shadow: $box-shadow

  &.is-mini
    max-width: 240px

  &.is-small
    max-width: 320px

  &.is-normal
    max-width: 480px

  &.is-large
    max-width: 640px

  &.is-xLarge
    max-width: 720px

  &.is-xxLarge
    max-width: 840px

  &.is-huge
    max-width: #{$global-contents-width-pc}px

  #{$root}--header,
  #{$root}--description,
  #{$root}--action:not(.is-close),
  #{$root}--footer
    margin-top: $gutter-size

    &:first-child
      margin-top: 0

  #{$root}--header
    grid-row: 1 / 2

  #{$root}--description
    grid-row: 2 / 3
    overflow-y: auto

  #{$root}--action
    &:not(.is-close)
      grid-row: 3 / 4

    &.is-close
      position: absolute
      top: 10px
      right: 10px
      z-index: 21

  #{$root}--footer
    grid-row: 4 / 5

  &.is-border
    $gutter-size: 16px

    #{$root}--header,
    #{$root}--description,
    #{$root}--action:not(.is-close),
    #{$root}--footer
      padding-top: $gutter-size
      border-top: 1px solid $border-x-light-gray

      &:first-child
        padding-top: 0
        border-top: none

    #{$root}--description
      padding-bottom: $gutter-size  // action の margin-top だと description のコンテンツ量が多く、スクロールが発生した際に、description と action との間に常に隙間ができてしまうので、description の bottom padding として action との間の隙間を空ける
      overscroll-behavior-y: none

      &:last-child
        padding-bottom: 0

    // 詳細度の関係でガターが入ってしまうので、再上書き
    #{$root}--action:not(.is-close)
      margin:
        top: 0 // description コメント参照
        bottom: -20px  // 基本 padding 40px だと広すぎるためネガティブマージンで調整

  &.is-lite
    padding: 0

    #{$root}--description
      padding:
        left: 0
        right: 0

  +breakpoint
    $modal-padding: 20px
    padding: $modal-padding

    #{$root}--action
      &.is-close
        top: -6px
        right: -6px

    &.is-border
      $gutter-size: 16px
      #{$root}--action:not(.is-close)
        margin-bottom: 0

// object
::v-deep .o-heading__modal
  $root: '.o-heading__modal'
  #{$root}--item
    font-weight: bold

::v-deep .o-action__close
  $root: '.o-action__close'

  #{$root}::before,
  #{$root}::after
    transition: opacity 0.5s ease-in-out, bottom 0.3s ease-in-out, visibility 0.5s ease-in-out

  $icon-size: 14px
  #{$root}--item
    +closeButtonStyle($icon-size, $border-dark-gray, 2px)
    +transition
    padding: 14px
    border-radius: 50%

    #{$root}::before,
    #{$root}::after
      transition: all 0.3s ease-out

    +mouseover
      transform: scale(1.2)
      background-color: rgba($bg-dark-gray, 1)
      box-shadow: $button-shadow

      &::before,
      &::after
        background-color: $bg-white

  +breakpoint
    #{$root}--item
      +closeButtonStyle($icon-size, $border-white, 2px)
      background-color: rgba($bg-gray, 1)
      border: 2px solid $border-white
</style>
